import {
    AppSettingsClient,
    DeferredPromise,
    useLogger,
} from '@amzn/event-engine-js-utils';
import { Auth, Hub } from 'aws-amplify';
import {
    QueryParamKey as SignInQueryParamKey,
    QueryParams as SignInQueryParams,
} from 'pages/SignIn';
import React, {
    createContext,
    FC,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { useHistory } from 'react-router-dom';

import { routes } from '../../constants';
import { AuthContextValue, AuthError, IdentityProvider } from './types';
import getSessionProviderName from './utils/get-session-provider-name';

export const AuthContext = createContext<AuthContextValue>(
    {} as AuthContextValue
);

const AuthProvider: FC = ({ children }) => {
    const history = useHistory();
    const { current: isAuthSetup } = useRef(new DeferredPromise<boolean>());
    const [authProviderLogger] = useLogger('AuthProviderLogger');
    const authListener = useCallback((data) => {
        switch (data.payload.event) {
            case 'configured':
                isAuthSetup.resolve(true);
                break;
        }
    }, []);

    const signIn = useCallback<AuthContextValue['signIn']>(
        async (identityProvider?: IdentityProvider, options = {}) => {
            await isAuthSetup.promise;

            let redirect = options.redirect;

            if (redirect === undefined) {
                const { pathname, search = '', hash = '' } = history.location;
                redirect = `${pathname}${search}${hash}`;
            }

            // If an identity provider is specified, the user should be redirected to
            // the specified provider directly. If no identity provider is specified,
            // present the user with available login providers on the sign-in page.

            if (!identityProvider) {
                const queryParams: SignInQueryParams = {
                    [SignInQueryParamKey.REDIRECT]: redirect,
                };

                history.replace(
                    `${routes.signIn}?${new URLSearchParams(
                        queryParams as URLSearchParams
                    ).toString()}`
                );
                return;
            }

            await Auth.federatedSignIn({
                customProvider: identityProvider,
                customState: redirect,
            });

            return;
        },
        []
    );

    const signOut = useCallback(async () => {
        await isAuthSetup.promise;

        return await Auth.signOut();
    }, []);

    const getCurrentSession = useCallback(
        async (identityProvider?: IdentityProvider) => {
            await isAuthSetup.promise;

            try {
                const currentSession = await Auth.currentSession();

                if (
                    identityProvider &&
                    identityProvider !== getSessionProviderName(currentSession)
                ) {
                    authProviderLogger.error(AuthError.WrongProvider);
                    return;
                }

                return currentSession;
            } catch (e) {
                authProviderLogger.error(e);
                return;
            }
        },
        []
    );

    useEffect(() => {
        Hub.listen('auth', authListener);

        const init = async () => {
            try {
                AppSettingsClient.getInstance().configureAuth();
            } catch (e) {
                authProviderLogger.error(e);
            }
        };

        init();

        return () => {
            Hub.remove('auth', authListener);
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{
                signIn,
                signOut,
                getCurrentSession,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
